/**
 * Local mock store for Product Announcements admin UI review.
 * Swap for api.announcementAdminApi when backend lands — keep the same shape.
 */

const STORAGE_KEY = 'wm_admin_announcements_v1'

export const ANNOUNCEMENT_STATUS = {
	DRAFT: 'draft',
	SCHEDULED: 'scheduled',
	LIVE: 'live',
	ARCHIVED: 'archived',
}

export const ANNOUNCEMENT_PRIORITY = {
	CRITICAL: 'critical',
	FEATURE: 'feature',
	TIP: 'tip',
}

export const ANNOUNCEMENT_PLATFORM = {
	WEB: 'web',
	IOS: 'ios',
	ANDROID: 'android',
}

export const ANNOUNCEMENT_SURFACE = {
	MODAL_CAROUSEL: 'modal_carousel',
	HOME_BANNER: 'home_banner',
	CHANGELOG: 'changelog',
}

const EMPTY_LOCALE = () => ({ title: '', body: '', ctaLabel: '' })

export const emptySlide = () => ({
	imageUrl: '',
	locales: {
		vi: { title: '', body: '' },
		en: { title: '', body: '' },
	},
})

export const emptyAnnouncementDraft = () => ({
	slug: '',
	status: ANNOUNCEMENT_STATUS.DRAFT,
	priority: ANNOUNCEMENT_PRIORITY.FEATURE,
	platforms: [
		ANNOUNCEMENT_PLATFORM.WEB,
		ANNOUNCEMENT_PLATFORM.IOS,
		ANNOUNCEMENT_PLATFORM.ANDROID,
	],
	// Start empty so content sections appear only after the admin picks where to show
	surfaces: [],
	startsAt: null,
	endsAt: null,
	ctaDeepLink: '',
	locales: {
		vi: EMPTY_LOCALE(),
		en: EMPTY_LOCALE(),
	},
	slides: [emptySlide()],
})

const seedAnnouncements = () => {
	const now = Date.now()
	return [
		{
			id: 1,
			slug: 'ielts-course-launch',
			status: ANNOUNCEMENT_STATUS.LIVE,
			priority: ANNOUNCEMENT_PRIORITY.FEATURE,
			platforms: ['web', 'ios', 'android'],
			surfaces: ['modal_carousel', 'home_banner', 'changelog'],
			startsAt: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
			endsAt: new Date(now + 30 * 24 * 60 * 60 * 1000).toISOString(),
			ctaDeepLink: '/courses/ielts-starter',
			locales: {
				vi: {
					title: 'Khóa học IELTS mới đã mở',
					body: 'Luyện từ vựng theo chủ đề IELTS với lộ trình rõ ràng. Bắt đầu ngay hôm nay.',
					ctaLabel: 'Vào học ngay',
				},
				en: {
					title: 'New IELTS course is live',
					body: 'Learn vocabulary by IELTS topics with a clear path. Start today.',
					ctaLabel: 'Start learning',
				},
			},
			slides: [
				{
					imageUrl:
						'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=80',
					locales: {
						vi: {
							title: 'Khóa học IELTS mới',
							body: 'Từ vựng theo chủ đề, flashcard và ôn tập thông minh.',
						},
						en: {
							title: 'New IELTS course',
							body: 'Topic vocabulary, flashcards, and smart review.',
						},
					},
				},
			],
			createdAt: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
			updatedAt: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
		},
		{
			id: 2,
			slug: 'pdf-reader-upgrade',
			status: ANNOUNCEMENT_STATUS.SCHEDULED,
			priority: ANNOUNCEMENT_PRIORITY.FEATURE,
			platforms: ['web'],
			surfaces: ['modal_carousel', 'changelog'],
			startsAt: new Date(now + 3 * 24 * 60 * 60 * 1000).toISOString(),
			endsAt: new Date(now + 45 * 24 * 60 * 60 * 1000).toISOString(),
			ctaDeepLink: 'https://pdf.wordsmine.com/',
			locales: {
				vi: {
					title: 'PDF Reader nâng cấp',
					body: 'Tra từ, dịch đoạn và lưu từ trực tiếp trong PDF.',
					ctaLabel: 'Mở PDF Reader',
				},
				en: {
					title: 'PDF Reader upgrade',
					body: 'Look up words, translate passages, and save vocab in PDFs.',
					ctaLabel: 'Open PDF Reader',
				},
			},
			slides: [
				{
					imageUrl:
						'https://images.unsplash.com/photo-1456513080080-7e9e7d8aa4c3?w=800&q=80',
					locales: {
						vi: {
							title: 'Đọc PDF thông minh',
							body: 'Highlight từ đã lưu và ghi chú ngay trên trang.',
						},
						en: {
							title: 'Smarter PDF reading',
							body: 'Highlight saved words and notes on the page.',
						},
					},
				},
				{
					imageUrl:
						'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&q=80',
					locales: {
						vi: {
							title: 'Dịch đoạn nhanh',
							body: 'Chọn đoạn văn để dịch và lưu ví dụ yêu thích.',
						},
						en: {
							title: 'Quick passage translate',
							body: 'Select text to translate and save favorite examples.',
						},
					},
				},
			],
			createdAt: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString(),
			updatedAt: new Date(now - 4 * 60 * 60 * 1000).toISOString(),
		},
		{
			id: 3,
			slug: 'seedbae-tip-draft',
			status: ANNOUNCEMENT_STATUS.DRAFT,
			priority: ANNOUNCEMENT_PRIORITY.TIP,
			platforms: ['web', 'ios', 'android'],
			surfaces: ['home_banner', 'changelog'],
			startsAt: null,
			endsAt: null,
			ctaDeepLink: '/seedbae',
			locales: {
				vi: {
					title: 'Mẹo dùng SeedBae',
					body: 'Hỏi SeedBae khi bạn kẹt nghĩa từ trong ngữ cảnh.',
					ctaLabel: 'Thử SeedBae',
				},
				en: {
					title: 'SeedBae tip',
					body: 'Ask SeedBae when a word’s meaning is unclear in context.',
					ctaLabel: 'Try SeedBae',
				},
			},
			slides: [emptySlide()],
			createdAt: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
			updatedAt: new Date(now - 6 * 24 * 60 * 60 * 1000).toISOString(),
		},
	]
}

const delay = (ms = 180) => new Promise(resolve => setTimeout(resolve, ms))

const readAll = () => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		if (!raw) {
			const seeded = seedAnnouncements()
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded))
			return seeded
		}
		const parsed = JSON.parse(raw)
		return Array.isArray(parsed) ? parsed : seedAnnouncements()
	} catch {
		return seedAnnouncements()
	}
}

const writeAll = items => {
	window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
}

const nextId = items =>
	items.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0) + 1

export const slugify = value =>
	String(value || '')
		.toLowerCase()
		.trim()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 80)

export const isValidHttpUrl = value => {
	const trimmed = String(value || '').trim()
	if (!trimmed) return false
	try {
		const url = new URL(trimmed)
		return url.protocol === 'http:' || url.protocol === 'https:'
	} catch {
		return false
	}
}

/** Deep link: absolute http(s) or app-relative path starting with / */
export const isValidDeepLink = value => {
	const trimmed = String(value || '').trim()
	if (!trimmed) return false
	if (trimmed.startsWith('/')) return trimmed.length > 1
	return isValidHttpUrl(trimmed)
}

/**
 * @param {{ page?: number, pageSize?: number, status?: string, platform?: string, priority?: string, q?: string }} filters
 */
export async function listAnnouncements(filters = {}) {
	await delay()
	const {
		page = 0,
		pageSize = 20,
		status,
		platform,
		priority,
		q,
	} = filters
	const query = String(q || '')
		.trim()
		.toLowerCase()

	let items = readAll().slice()
	items.sort(
		(a, b) =>
			new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
	)

	if (status) {
		items = items.filter(item => item.status === status)
	}
	if (platform) {
		items = items.filter(
			item =>
				Array.isArray(item.platforms) && item.platforms.includes(platform)
		)
	}
	if (priority) {
		items = items.filter(item => item.priority === priority)
	}
	if (query) {
		items = items.filter(item => {
			const hay = [
				item.slug,
				item.locales?.vi?.title,
				item.locales?.en?.title,
				item.ctaDeepLink,
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()
			return hay.includes(query)
		})
	}

	const total = items.length
	const start = page * pageSize
	const data = items.slice(start, start + pageSize)
	return { data, total }
}

export async function getAnnouncement(id) {
	await delay()
	const item = readAll().find(row => Number(row.id) === Number(id))
	if (!item) {
		const err = new Error('Announcement not found.')
		err.code = 'NOT_FOUND'
		throw err
	}
	return JSON.parse(JSON.stringify(item))
}

export async function createAnnouncement(payload) {
	await delay(220)
	const items = readAll()
	const slug = slugify(payload.slug)
	if (items.some(item => item.slug === slug)) {
		const err = new Error(`Slug "${slug}" is already used.`)
		err.code = 'DUPLICATE_SLUG'
		throw err
	}
	const now = new Date().toISOString()
	const created = {
		...payload,
		id: nextId(items),
		slug,
		createdAt: now,
		updatedAt: now,
	}
	items.unshift(created)
	writeAll(items)
	return created
}

export async function updateAnnouncement(id, payload) {
	await delay(220)
	const items = readAll()
	const index = items.findIndex(row => Number(row.id) === Number(id))
	if (index < 0) {
		const err = new Error('Announcement not found.')
		err.code = 'NOT_FOUND'
		throw err
	}
	const slug = slugify(payload.slug)
	if (items.some(item => item.slug === slug && Number(item.id) !== Number(id))) {
		const err = new Error(`Slug "${slug}" is already used.`)
		err.code = 'DUPLICATE_SLUG'
		throw err
	}
	const updated = {
		...items[index],
		...payload,
		id: Number(id),
		slug,
		createdAt: items[index].createdAt,
		updatedAt: new Date().toISOString(),
	}
	items[index] = updated
	writeAll(items)
	return updated
}

export async function deleteAnnouncement(id) {
	await delay(160)
	const items = readAll()
	const next = items.filter(row => Number(row.id) !== Number(id))
	if (next.length === items.length) {
		const err = new Error('Announcement not found.')
		err.code = 'NOT_FOUND'
		throw err
	}
	writeAll(next)
	return { ok: true }
}

export async function duplicateAnnouncement(id) {
	await delay(200)
	const source = await getAnnouncement(id)
	const items = readAll()
	let baseSlug = `${source.slug}-copy`
	let slug = baseSlug
	let n = 2
	while (items.some(item => item.slug === slug)) {
		slug = `${baseSlug}-${n}`
		n += 1
	}
	const now = new Date().toISOString()
	const copy = {
		...source,
		id: nextId(items),
		slug,
		status: ANNOUNCEMENT_STATUS.DRAFT,
		createdAt: now,
		updatedAt: now,
	}
	items.unshift(copy)
	writeAll(items)
	return copy
}

/** Reset mock data to seed (dev/review helper). */
export function resetAnnouncementMockStore() {
	writeAll(seedAnnouncements())
}
